import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoDirective } from '@jsverse/transloco';
import { map, timer } from 'rxjs';

import { Logo } from '../../../shared/components/logo/logo';

/** One line of the spec-sheet list at the bottom of the display screen. */
export interface ErrorPageRow {
  label: string;
  value: string;
  /** Render the value as the black e-paper status chip (e.g. "Failed"). */
  badge?: boolean;
}

/**
 * Shared full-screen shell for the error pages (design "Error Pages" 3a): the
 * error is rendered on a Papercast e-paper display — dark bezel, black header
 * strip with the live time, error code + message on the screen, and a
 * spec-sheet row list at the bottom. The page's actions project beneath the
 * display. Pages pass already-translated strings; the shell owns only the
 * display chrome (header brand + clock).
 *
 * The display greys are hardcoded on purpose: they depict physical e-paper
 * hardware, so the screen looks identical in light and dark theme — only the
 * page behind it themes.
 */
@Component({
  selector: 'app-error-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, TranslocoDirective, Logo],
  template: `
    <ng-container *transloco="let t; read: 'errors.display'">
      <div
        class="flex min-h-screen flex-col items-center justify-center gap-8 bg-page px-4 py-12"
      >
        <!-- Bezel -->
        <div class="max-w-full rounded-[12px] bg-[#262626] p-4 shadow-raised">
          <!-- E-paper screen: fixed at the design's 640x380 on desktop, grows
               downward when narrow viewports wrap the text. -->
          <div
            class="flex min-h-[380px] w-[640px] max-w-full flex-col overflow-hidden rounded-[2px] bg-[#F0F0F0] text-[#1A1A1A]"
          >
            <div
              class="flex h-11 flex-none items-center justify-between bg-[#1A1A1A] px-5 font-display text-sm font-bold uppercase tracking-[0.03em] text-white"
            >
              <span class="flex items-center gap-2.5">
                <app-logo variant="mark" [height]="20" />
                {{ t('header') }}
              </span>
              <span>{{ now() | date: 'HH:mm' }}</span>
            </div>

            <div class="flex flex-1 flex-col justify-between gap-6 p-6">
              <div class="flex flex-col items-start gap-4 sm:flex-row sm:gap-6">
                @if (code(); as code) {
                  <span class="font-display text-[88px] font-bold leading-[80px]">{{
                    code
                  }}</span>
                }
                <div class="flex flex-col gap-2">
                  @if (eyebrow(); as eyebrow) {
                    <span
                      class="font-display text-sm font-bold uppercase tracking-[0.03em]"
                      >{{ eyebrow }}</span
                    >
                  }
                  <h1 class="font-display text-[28px] font-bold leading-8">{{ title() }}</h1>
                  <p class="text-base leading-6">{{ body() }}</p>
                </div>
              </div>

              @if (rows().length > 0) {
                <div class="flex flex-col text-[15px] leading-5">
                  @for (row of rows(); track row.label) {
                    <div class="spec-row flex min-h-[38px] items-center justify-between gap-4">
                      <span>{{ row.label }}</span>
                      @if (row.badge) {
                        <b class="bg-[#1A1A1A] px-2 py-[2px] text-[#F0F0F0]">{{ row.value }}</b>
                      } @else {
                        <b class="text-right">{{ row.value }}</b>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </div>

        <div class="flex flex-wrap justify-center gap-3">
          <ng-content />
        </div>
      </div>
    </ng-container>
  `,
  styles: `
    /* Hairline between spec rows (design: inset bottom shadow on all but the
       last) — same fixed alpha as the rest of the e-paper artwork. */
    .spec-row + .spec-row {
      box-shadow: inset 0 1px 0 rgba(0, 0, 0, 0.2);
    }
  `,
})
export class ErrorPage {
  /** Big error code on the screen (e.g. "404"); omitted on the welcome-style page. */
  readonly code = input<string>();
  /** Small uppercase line above the title (used when there is no code). */
  readonly eyebrow = input<string>();
  readonly title = input.required<string>();
  readonly body = input.required<string>();
  /** Spec-sheet rows at the bottom of the screen. */
  readonly rows = input<ErrorPageRow[]>([]);

  /** Header clock — e-paper displays show the time, so does this one. */
  protected readonly now = toSignal(timer(0, 60_000).pipe(map(() => new Date())), {
    initialValue: new Date(),
  });
}
